/** Actions ▾ — lancer, pause, fin. */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ActionChevron } from "../../components/ActionChevron";
import { Button } from "../../components/Button";
import { placeFixedMenu, watchMenuPlace } from "../../components/placeMenu";
import { IcoCopy, IcoEdit, IcoEye, IcoInvoice, IcoPause, IcoRepeat, IcoStop, IcoTrash } from "../quotes/QuoteMenuIcons";

const MENU_W = 260;

type RecurringMenuProps = {
  status: string;
  busy?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onEdit?: () => void;
  onPdf?: () => void;
  onCloneInvoice?: () => void;
  onDuplicate?: () => void;
  onLaunch?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  onDelete?: () => void;
};

export function RecurringMenu({
  status,
  busy,
  open: openProp,
  onOpenChange,
  onEdit,
  onPdf,
  onCloneInvoice,
  onDuplicate,
  onLaunch,
  onPause,
  onEnd,
  onDelete,
}: RecurringMenuProps) {
  const box = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [inner, setInner] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 420 });
  const open = openProp ?? inner;

  function setOpen(next: boolean) {
    if (onOpenChange) onOpenChange(next);
    if (openProp === undefined) setInner(next);
  }

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = box.current?.querySelector("button")?.getBoundingClientRect();
      if (!r) return;
      setPos(placeFixedMenu(r, MENU_W, menu.current?.scrollHeight || 320));
    };
    const stop = watchMenuPlace(place);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (box.current?.contains(t) || menu.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => {
      stop();
      document.removeEventListener("mousedown", onDoc);
    };
  }, [open]);

  function row(fn: (() => void) | undefined, ico: ReactNode, text: string, tone?: "danger" | "lead") {
    if (!fn) return null;
    return (
      <button
        type="button"
        className={["fx-quote-menu__item", tone === "danger" ? "is-danger" : "", tone === "lead" ? "is-lead" : ""].filter(Boolean).join(" ")}
        role="menuitem"
        onClick={() => {
          setOpen(false);
          fn();
        }}
      >
        {ico} {text}
      </button>
    );
  }

  const launch = status === "draft" || status === "paused" ? row(onLaunch, <IcoRepeat />, "Lancer", "lead") : null;
  const pause = status === "running" ? row(onPause, <IcoPause />, "Mettre en pause") : null;
  const end = status !== "ended" ? row(onEnd, <IcoStop />, "Fin", "danger") : null;
  const del = status === "draft" ? row(onDelete, <IcoTrash />, "Supprimer", "danger") : null;
  const consult = onEdit || onPdf;
  const copies = onCloneInvoice || onDuplicate;
  const closing = pause || end || del;

  return (
    <div className="fx-quote-menu" ref={box}>
      <Button className="fx-chev-btn" disabled={busy} aria-expanded={open} onClick={() => setOpen(!open)}>
        Actions <ActionChevron />
      </Button>
      {open
        ? createPortal(
            <div
              ref={menu}
              className="fx-quote-menu__list"
              role="menu"
              style={{ top: pos.top, left: pos.left, width: MENU_W, maxHeight: pos.maxHeight, overflowY: "auto" }}
            >
              {launch}
              {launch && (consult || copies || closing) ? <div className="fx-quote-menu__sep" role="separator" /> : null}
              {row(onEdit, <IcoEdit />, "Éditer")}
              {row(onPdf, <IcoEye />, "Voir le PDF")}
              {consult && copies ? <div className="fx-quote-menu__sep" role="separator" /> : null}
              {row(onCloneInvoice, <IcoInvoice />, "Cloner en facture")}
              {row(onDuplicate, <IcoCopy />, "Dupliquer")}
              {(consult || copies) && closing ? <div className="fx-quote-menu__sep" role="separator" /> : null}
              {pause}
              {end}
              {del}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
